import * as motion from "motion/react-client";
import { useTranslation } from "react-i18next";

import { cn } from "../../lib/utils.js";
import type { Scale } from "./SideBoard.js";

/**
 * What somebody said the card was — an announcement, not a footnote.
 *
 * # It is the thing the room is asked to have an opinion about
 *
 * Phase 18's finding #1 was that nobody could see the active player's answer, so
 * nobody could argue with it: *"we can't challenge the active player's guess."*
 * That was fixed by putting it on `SideBoard` — and then 20d found the two
 * halves it had still not reached.
 *
 * 🔴 **It was unreadable.** One line of `text-xs` on a phone, and `text-lg` on a
 * *television*, which is 18px read from three metres: *"right now it is barely
 * noticeable and in some cases can be missed."* 20d also made the server publish
 * it a phase earlier, which is wasted if nobody sees it.
 *
 * 🔴 **And it was absent from the one screen that exists to judge it.** The
 * challenge stage draws a bare, aimable `Timeline` rather than a `SideBoard`, so
 * a challenger could see *where* the card was put and never *what it was called*
 * — which is finding #1 again, on the screen it was reported about. This
 * component exists so both surfaces render one thing rather than two that drift.
 *
 * # ⚠️ Neutral, deliberately
 *
 * No `flash`, no `correct`: a claim that borrowed the accent the revealed year
 * uses would read as *right*, and this is a thing somebody said, not a verdict.
 * The word carries the meaning, so nothing depends on the styling — the rule
 * that no state is signalled by colour alone.
 *
 * One spring on mount and nothing on idle, the same budget `Card` spends. It
 * runs when the claim first appears, because the element does not exist before
 * the grids close.
 */
export function GuessClaim({
  artist,
  title,
  scale = "near",
  className,
}: {
  /** The artist they picked — `null` is a real answer and reads as "no answer". */
  artist: string | null;
  /** The title they picked, likewise. */
  title: string | null;
  /** How far away the reader is. */
  scale?: Scale;
  className?: string;
}) {
  const { t } = useTranslation();
  const room = scale === "room";

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: "spring", stiffness: 380, damping: 30 }}
      data-testid="side-guess"
      className={cn(
        "rounded-xl border border-white/15 bg-white/[0.06]",
        room ? "px-5 py-3" : "px-3 py-2",
        className,
      )}
    >
      <p
        className={cn(
          "font-semibold uppercase tracking-[0.2em] text-white/45",
          room ? "text-base" : "text-[10px]",
        )}
      >
        {t("side.guessed")}
      </p>
      <p
        className={cn(
          "flex flex-wrap items-baseline gap-x-2 font-semibold leading-tight",
          room ? "text-4xl" : "text-base",
        )}
      >
        <span>{artist ?? t("side.noAnswer")}</span>
        <span className="text-white/25">·</span>
        <span>{title ?? t("side.noAnswer")}</span>
      </p>
    </motion.div>
  );
}
